import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Card, Alert, Spinner, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import logo from '../../../../images/logo.png';
import { getCompanyInfo } from '../../../../services/PublicOrderService';
import StepBranch from './steps/StepBranch';
import StepProducts from './steps/StepProducts';
import StepCustomerInfo from './steps/StepCustomerInfo';
import StepConfirmation from './steps/StepConfirmation';
import './PublicOrder.css';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const STEPS = {
    BRANCH: 0,
    PRODUCTS: 1,
    CUSTOMER: 2,
    CONFIRMATION: 3,
};

const stepLabels = ['Filial', 'Produtos', 'Seus Dados', 'Confirmação'];

const PublicOrderPage = () => {
    const { t } = useTranslation();
    const { companyUuid } = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(STEPS.BRANCH);

    const [companyInfo, setCompanyInfo] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [cart, setCart] = useState({});
    const [customerData, setCustomerData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        document: '',
    });
    const [obs, setObs] = useState('');
    const [orderResult, setOrderResult] = useState(null);

    // Título da aba
    useEffect(() => {
        if (companyInfo?.name) {
            document.title = `Pedido — ${companyInfo.name}`;
        } else {
            document.title = 'Faça seu pedido';
        }
        return () => {
            document.title = 'Colmeia';
        };
    }, [companyInfo]);

    const loadCompanyInfo = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getCompanyInfo(companyUuid);
            setCompanyInfo(response.data);

            // Auto-skip filial se só tiver uma
            if (response.data.branches && response.data.branches.length === 1) {
                setSelectedBranch(response.data.branches[0]);
                setCurrentStep(STEPS.PRODUCTS);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError('Empresa não encontrada. Verifique o link e tente novamente.');
            } else {
                setError('Não foi possível carregar as informações. Tente novamente mais tarde.');
            }
        } finally {
            setLoading(false);
        }
    }, [companyUuid]);

    useEffect(() => {
        if (!companyUuid) {
            setError('Link inválido.');
            setLoading(false);
            return;
        }

        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(companyUuid)) {
            setError('Link inválido. O identificador da empresa não é válido.');
            setLoading(false);
            return;
        }

        loadCompanyInfo();
    }, [companyUuid, loadCompanyInfo]);

    const goToStep = (step) => setCurrentStep(step);
    const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, STEPS.CONFIRMATION));
    const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, STEPS.BRANCH));

    const handleSelectBranch = (branch) => {
        setSelectedBranch(branch);
        setCart({});
        nextStep();
    };

    const handleCustomerSubmit = (data) => {
        setCustomerData(data);
        nextStep();
    };

    const handleOrderCreated = (result) => {
        setOrderResult(result);
    };

    const handleNewOrder = () => {
        setCart({});
        setCustomerData({ firstName: '', lastName: '', phone: '', document: '' });
        setObs('');
        setOrderResult(null);
        setCurrentStep(STEPS.BRANCH);

        if (companyInfo?.branches?.length === 1) {
            setSelectedBranch(companyInfo.branches[0]);
            setCurrentStep(STEPS.PRODUCTS);
        } else {
            setSelectedBranch(null);
        }
    };

    // ── Loading ──────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="public-order-page">
                <Container className="py-5">
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Carregando...</p>
                    </div>
                </Container>
            </div>
        );
    }

    // ── Erro ─────────────────────────────────────────────────────────────
    if (error) {
        return (
            <div className="public-order-page">
                <Container className="py-5">
                    <div className="text-center mb-4">
                        <img src={logo} alt="Logo" style={{ maxHeight: 50 }} />
                    </div>
                    <Alert variant="danger" className="text-center">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                </Container>
            </div>
        );
    }

    // Destaques (itens selecionados, botões, foco) seguem a cor da marca; sem cor, cinza neutro (#333).
    const orderBrand = companyInfo?.headerColor || '#333';
    const brandVars = {
        '--order-brand': orderBrand,
        '--order-brand-strong': `color-mix(in srgb, ${orderBrand} 82%, #000)`,
    };

    return (
        <div className="public-order-page" style={brandVars}>
            {/* Hero */}
            <div
                className="order-hero"
                style={companyInfo?.headerColor ? { background: companyInfo.headerColor } : undefined}
            >
                <div className="order-hero-logo">
                    {companyInfo?.logoUrl ? (
                        <img src={resolvePublicAssetUrl(companyInfo.logoUrl)} alt={companyInfo.name} />
                    ) : (
                        <img src={logo} alt="Logo" />
                    )}
                </div>
                <h1 className="order-hero-name">{companyInfo?.name}</h1>
                <p className="order-hero-sub">Faça seu pedido</p>
                <Button
                    as={Link}
                    to={`/order/${companyUuid}/track`}
                    variant="light"
                    size="sm"
                    className="order-track-link"
                >
                    <i className="fa fa-search me-2"></i>
                    Acompanhar pedido
                </Button>
            </div>

            <Container className="py-4 booking-container">
                {/* Progress bar — oculto na tela de confirmação final */}
                {currentStep < STEPS.CONFIRMATION && (
                    <div className="booking-progress mb-4">
                        <div className="progress-steps">
                            {stepLabels.slice(0, -1).map((label, index) => (
                                <div
                                    key={index}
                                    className={`progress-step${index <= currentStep ? ' active' : ''}${index < currentStep ? ' completed' : ''}`}
                                    onClick={() => index < currentStep && goToStep(index)}
                                >
                                    <div className="step-number">
                                        {index < currentStep ? (
                                            <i className="fa fa-check"></i>
                                        ) : (
                                            index + 1
                                        )}
                                    </div>
                                    <span className="step-name">{label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Card principal */}
                <Card className="booking-card shadow-lg">
                    <Card.Body className="p-4">
                        {currentStep === STEPS.BRANCH && (
                            <StepBranch
                                branches={companyInfo?.branches || []}
                                onSelect={handleSelectBranch}
                            />
                        )}

                        {currentStep === STEPS.PRODUCTS && (
                            <StepProducts
                                companyUuid={companyUuid}
                                branchId={selectedBranch?.id}
                                cart={cart}
                                onCartChange={setCart}
                                onNext={nextStep}
                                onBack={prevStep}
                            />
                        )}

                        {currentStep === STEPS.CUSTOMER && (
                            <StepCustomerInfo
                                companyUuid={companyUuid}
                                customerData={customerData}
                                onSubmit={handleCustomerSubmit}
                                onBack={prevStep}
                            />
                        )}

                        {currentStep === STEPS.CONFIRMATION && (
                            <StepConfirmation
                                companyUuid={companyUuid}
                                branch={selectedBranch}
                                cart={cart}
                                customerData={customerData}
                                obs={obs}
                                onObsChange={setObs}
                                orderResult={orderResult}
                                onConfirm={handleOrderCreated}
                                onBack={prevStep}
                                onNewOrder={handleNewOrder}
                            />
                        )}
                    </Card.Body>
                </Card>

                <PublicBrandSignature />
            </Container>
        </div>
    );
};

export default PublicOrderPage;
